import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BookingForm from './BookingForm';

function WorkshopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchWorkshop();
  }, [id]);

  const fetchWorkshop = async () => {
    try {
      const workshopDoc = await getDoc(doc(db, 'sessions_workshops', id));
      
      if (!workshopDoc.exists()) {
        setError('Workshop not found');
        return;
      }

      const workshopData = { id: workshopDoc.id, ...workshopDoc.data() };
      setWorkshop(workshopData);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      setError('Error loading workshop');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const safeJoin = (array, separator = ', ') => {
    if (!Array.isArray(array)) return 'N/A';
    return array.filter(item => item && item.toString().trim()).join(separator);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-900">Loading workshop...</div>
      </div>
    );
  }

  if (error || !workshop) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg p-8 border border-gray-300">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error || 'Workshop Not Found'}
            </h1>
            <Link
              to="/"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 border border-blue-600"
            >
              Back to Workshops
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (showBookingForm) {
    return (
      <BookingForm 
        workshop={workshop}
        onComplete={() => setShowBookingForm(false)}
        onCancel={() => setShowBookingForm(false)}
      />
    );
  }
  const spotsLeft = workshop.maxParticipants - (workshop.currentParticipants || 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 mb-8"
        >
          ← Back to workshops
        </Link>

        {/* Hero Section */}
        <div className="mb-12">
          <div className="relative h-80 bg-gray-200 mb-6">
            <img
              src={workshop.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop'}
              alt={workshop.title}
              className="w-full h-full object-cover"
            />
            
            {/* Availability Badge */}
            <div className="absolute top-6 left-6">
              <span className="px-3 py-2 text-sm font-medium bg-white text-gray-900 border border-gray-300">
                {spotsLeft} spots remaining
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-gray-900">
              {workshop.title}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl">
              {workshop.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-white border border-gray-300">
                <div className="text-xs font-medium text-gray-500 mb-1">Location</div>
                <div className="text-sm font-medium text-gray-900">{workshop.location || 'TBD'}</div>
              </div>

              <div className="p-4 bg-white border border-gray-300">
                <div className="text-xs font-medium text-gray-500 mb-1">Duration</div>
                <div className="text-sm font-medium text-gray-900">
                  {workshop.startDate && workshop.endDate ? 
                    `${formatDate(workshop.startDate)} - ${formatDate(workshop.endDate)}` : 
                    'TBD'
                  }
                </div>
              </div>

              <div className="p-4 bg-white border border-gray-300">
                <div className="text-xs font-medium text-gray-500 mb-1">Max Participants</div>
                <div className="text-sm font-medium text-gray-900">{workshop.maxParticipants || 'TBD'}</div>
              </div>

              <div className="p-4 bg-white border border-gray-300">
                <div className="text-xs font-medium text-gray-500 mb-1">Price</div>
                <div className="text-sm font-medium text-gray-900">
                  {workshop.price ? formatPrice(workshop.price) : 'TBD'}
                </div>
              </div>
            </div>

            {/* Tabbed Content */}
            <div className="bg-white border border-gray-300">
              {/* Tab Navigation */}
              <div className="border-b border-gray-300">
                <nav className="flex">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'itinerary', label: 'Itinerary' },
                    { id: 'gear', label: 'Gear List' },
                    { id: 'policies', label: 'Policies' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-4 text-sm font-medium border-b-2 ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Workshop Overview</h3>
                    <p className="text-gray-600">{workshop.description}</p>
                  </div>
                )}

                {activeTab === 'itinerary' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Itinerary</h3>
                    <p className="text-gray-600">{workshop.itinerary || 'Itinerary details coming soon...'}</p>
                  </div>
                )}

                {activeTab === 'gear' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Required Gear</h3>
                    <p className="text-gray-600">{workshop.requiredGear || 'Gear list coming soon...'}</p>
                  </div>
                )}

                {activeTab === 'policies' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Policies</h3>
                    <p className="text-gray-600">{workshop.policies || 'Policies coming soon...'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 border border-gray-300 rounded-lg sticky top-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Price</h3>
                  <div className="text-2xl font-bold text-gray-900">
                    {workshop.price ? formatPrice(workshop.price) : 'TBD'}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Availability</h3>
                  <div className="text-sm text-gray-600">
                    {spotsLeft > 0 ? `${spotsLeft} spots remaining` : 'Workshop is full'}
                  </div>
                </div>

                <button
                  onClick={() => setShowBookingForm(true)}
                  disabled={spotsLeft <= 0}
                  className="w-full py-3 px-4 font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-600"
                >
                  {spotsLeft > 0 ? 'Book Workshop' : 'Workshop Full'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkshopDetail; 